/**
 * Text edits behind the markdown editor's toolbar. Each takes the text and the
 * selection and returns the new text and where the selection goes, so the
 * editor stays a plain textarea (undo, IME and screen readers keep working).
 */
export type MarkdownEdit = { text: string; selectionStart: number; selectionEnd: number };

/** Put `before`/`after` around the selection, or around `placeholder` when nothing is selected. */
export function wrapSelection(text: string, start: number, end: number, before: string, after: string, placeholder: string): MarkdownEdit {
  const selected = text.slice(start, end);
  // Already wrapped: take the markers off.
  if (selected && text.slice(start - before.length, start) === before && text.slice(end, end + after.length) === after) {
    const next = text.slice(0, start - before.length) + selected + text.slice(end + after.length);
    return { text: next, selectionStart: start - before.length, selectionEnd: end - before.length };
  }
  const inner = selected || placeholder;
  const next = text.slice(0, start) + before + inner + after + text.slice(end);
  return { text: next, selectionStart: start + before.length, selectionEnd: start + before.length + inner.length };
}

function lineBounds(text: string, start: number, end: number): [number, number] {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const newline = text.indexOf('\n', end > start && text[end - 1] === '\n' ? end - 1 : end);
  return [lineStart, newline === -1 ? text.length : newline];
}

/**
 * Start every selected line with `prefix` (`# `, `- `, `> `…). `numbered`
 * counts 1., 2., 3. When every line already has the prefix it is removed.
 */
export function prefixLines(text: string, start: number, end: number, prefix: string, options: { numbered?: boolean } = {}): MarkdownEdit {
  const [lineStart, lineEnd] = lineBounds(text, start, end);
  const lines = text.slice(lineStart, lineEnd).split('\n');
  const pattern = options.numbered ? /^\d+\.\s/ : new RegExp('^' + prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const remove = lines.every((line) => pattern.test(line));
  const changed = lines.map((line, index) => {
    if (remove) return line.replace(pattern, '');
    const marker = options.numbered ? `${index + 1}. ` : prefix;
    return marker + line.replace(/^(#{1,6}\s|[-*]\s(\[[ x]\]\s)?|>\s|\d+\.\s)/, '');
  });
  const block = changed.join('\n');
  const next = text.slice(0, lineStart) + block + text.slice(lineEnd);
  return { text: next, selectionStart: lineStart, selectionEnd: lineStart + block.length };
}

/** A fenced code block around the selected lines, or an empty one to type into. */
export function codeBlock(text: string, start: number, end: number): MarkdownEdit {
  const selected = text.slice(start, end);
  const before = start > 0 && text[start - 1] !== '\n' ? '\n' : '';
  const body = selected || 'code';
  const inserted = `${before}\`\`\`\n${body}\n\`\`\`\n`;
  const next = text.slice(0, start) + inserted + text.slice(end);
  const bodyStart = start + before.length + 4;
  return { text: next, selectionStart: bodyStart, selectionEnd: bodyStart + body.length };
}

/** `[text](url)` with the url selected so it can be typed over. */
export function link(text: string, start: number, end: number): MarkdownEdit {
  const label = text.slice(start, end) || 'link text';
  const url = 'https://';
  const inserted = `[${label}](${url})`;
  const next = text.slice(0, start) + inserted + text.slice(end);
  const urlStart = start + label.length + 3;
  return { text: next, selectionStart: urlStart, selectionEnd: urlStart + url.length };
}

export type Passage = { start: number; end: number; text: string };

/**
 * Put `replacement` where `passage` was, if that text is still there: at the
 * same place, or else its only occurrence. Null when it changed or appears more
 * than once, so a suggestion never lands on text the person has since edited.
 */
export function replacePassage(text: string, passage: Passage, replacement: string): string | null {
  if (text.slice(passage.start, passage.end) === passage.text) {
    return text.slice(0, passage.start) + replacement + text.slice(passage.end);
  }
  const at = text.indexOf(passage.text);
  if (at === -1 || text.indexOf(passage.text, at + 1) !== -1) return null;
  return text.slice(0, at) + replacement + text.slice(at + passage.text.length);
}
